import { DateTimeFormatOptions } from '@intlify/core-base';
import { i18n } from '@/plugins/i18n';
import { IDocumentDate } from '@/interfaces/IDocumentDate';
import { checkmarkCircleOutline, closeCircleOutline, warningOutline } from 'ionicons/icons';

export function formatDateTime(
  date: string,
  locale: string,
  isUtc: boolean = false,
  short: boolean = false,
) {
  const options: DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  if (!short) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
  }

  if (isUtc) {
    options.timeZone = 'UTC';
  }

  return new Date(date).toLocaleDateString(locale, options);
}

export const dateInTenDays = () => {
  const today = new Date(); // Get the current date
  today.setDate(today.getDate() + 10); // Add 10 days

  // Get the month, day, and year
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();

  // Return the formatted date
  return `${month}-${day}-${year}`;
};

export function formatDateYMD(dateString: Date): string {
  if (!dateString) {
    return '';
  }
  const year = dateString.getFullYear();
  const month = String(dateString.getMonth() + 1).padStart(2, '0');
  const day = String(dateString.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const todayDate = () => {
  return new Date();
};

export const oneYearFromNow = () => {
  return new Date(new Date().setFullYear(new Date().getFullYear() + 1));
};

export const getRangeDates = (daysToDiscount: number) => {
  const actualHour = new Date();
  const endDate = actualHour.toISOString().slice(0, 10);

  actualHour.setDate(actualHour.getDate() - daysToDiscount);
  const startDate = actualHour.toISOString().slice(0, 10);

  return { startDate, endDate };
};

export const getRangeMothDates = () => {
  const actualDate = new Date();
  actualDate.setDate(1);
  const startDate = actualDate.toISOString().slice(0, 10);
  const endDate = new Date().toISOString().slice(0, 10);

  return { startDate, endDate };
};

export const daysBetweenDates = (startDate: string, endDate: string) => {
  const dayInMilliseconds = 24 * 60 * 60 * 1000; // Milisegundos en un día
  const differenceMilliseconds = new Date(endDate).getTime() - new Date(startDate).getTime();
  return Math.round(differenceMilliseconds / dayInMilliseconds);
};

export const calculateAge = (birthDate: string): number => {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export const getExpirationMessage = (expirationDate: string): IDocumentDate => {
  const expiryDate = new Date(expirationDate);
  const currentDate = new Date();

  const timeDiff = expiryDate.getTime() - currentDate.getTime();

  const diffInMonths = timeDiff / (1000 * 3600 * 24 * 30); // Approximate days per month

  if (timeDiff < 0) {
    return {
      color: 'danger',
      icon: closeCircleOutline,
      title: i18n.global.t('files.expires.expired'),
    };
  }

  if (diffInMonths > 12) {
    return {
      color: 'primary',
      icon: checkmarkCircleOutline,
      title: i18n.global.t('files.expires.expiresMoreTwelveMonths'),
    };
  } else if (diffInMonths >= 6) {
    return {
      color: 'success',
      icon: checkmarkCircleOutline,
      title: i18n.global.t('files.expires.expiresMoreSixMonths'),
    };
  } else if (diffInMonths >= 3) {
    return {
      color: 'warning',
      icon: warningOutline,
      title: i18n.global.t('files.expires.expiresMoreThreeMonths'),
    };
  } else {
    return {
      color: 'warning',
      icon: warningOutline,
      title: i18n.global.t('files.expires.expiresLessThreeMonths'),
    };
  }
};
